import { HttpMethod } from './enum/index.js';
/**
 * Wrapper function around fetch that abstracts away the try/catch block for error
 * handling.
 *
 * @param { string } url - the URL to be fetched
 * @param { RequestInit } init - initialization options for the fetch call
 * @returns { Promise } - resolves with a Response object representing the response to your request
 *
 */
export const fetchAdapter = async (url, init) => {
    try {
        return await fetch(url, init);
    }
    catch (err) {
        throw err;
    }
};
/**
 * Generic function to fetch/POST data using the fetchAdapter function and handle
 * errors
 *
 * @param { string } url - the URL to make the API request
 * @param { RequestInit } options - initialization options for the request (method, headers, etc.)
 * @returns { Promise } - a promise that returns the Response object or throws an error
 *
 */
const request = async (url, options) => {
    try {
        const response = await fetchAdapter(url, options);
        if (!response.ok) {
            console.warn(`received error status code ${response.status}`);
        }
        return response;
    }
    catch (err) {
        throw err;
    }
};
/**
 * Dictionary of HTTP headers for JSON requests
 */
const JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
};
/**
 * `ajaxRequestor`
 * Provides GET and POST functionality to consumer with a an adapter for
 * the JS `fetch` function.
 */
const ajaxRequestor = {
    /**
     * Sends a GET request to the given URL using the provided options object.
     *
     * @param { string } url - the URL to make the GET request to
     * @param { RequestInit } options - initialization options for the request
     * @param { boolean } isJSON - set to true if the response is JSON
     * @returns { T | void } - the JSON data returned by the server or undefined
     */
    get: async (url, options, isJSON = false) => {
        try {
            const headers = isJSON ? JSON_HEADERS : {};
            const init = {
                method: HttpMethod.GET,
                headers,
                ...options
            };
            const response = await request(url, init);
            if (!response.ok) {
                throw new Error(`ERROR with get from ${url}, response status = ${response.status}`);
            }
            if (!isJSON) {
                return undefined;
            }
            return (await response.json());
        }
        catch (err) {
            throw err;
        }
    },
    /**
     * Sends a POST request to the given URL using the provided options object.
     *
     * @param { string } url - the URL to make the POST request to
     * @param { RequestInit } options - initialization options for the request
     * @param { boolean } isJSON - whether the request needs to send JSON or not
     * @returns { T | void } - the JSON data returned by the server or undefined
     */
    post: async (url, options, isJSON = false) => {
        try {
            const headers = isJSON ? JSON_HEADERS : {};
            options = {
                ...options,
                method: HttpMethod.POST,
                headers
            };
            const response = await request(url, options);
            if (!response.ok) {
                console.warn(`ERROR with post to ${url}, response status = ${response.status}`);
            }
            if (!isJSON) {
                return undefined;
            }
            if (response.status === 204) {
                return response;
            }
            return (await response.json());
        }
        catch (err) {
            throw err;
        }
    }
};
export const { get, post } = ajaxRequestor;
export default ajaxRequestor;
